/**
 * Central monitoring and logging system for IMKI
 * Supports structured logging with namespaces, file storage, and Slack notifications (Phase 5)
 */

import fs from 'fs'
import path from 'path'

interface ErrorLog {
  timestamp: Date
  level: 'error' | 'warn' | 'info' | 'debug'
  namespace: string
  message: string
  context?: Record<string, unknown>
  userId?: string
  stack?: string
}

const SLACK_WEBHOOK_URL = process.env.SLACK_WEBHOOK_URL
const LOG_DIR = process.env.LOG_DIR || (process.env.NODE_ENV === 'production' ? '/var/log/imki' : './logs')
const MAX_LOG_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_LOG_FILES = 5

/**
 * Ensure log directory exists
 */
function ensureLogDir() {
  if (!fs.existsSync(LOG_DIR)) {
    fs.mkdirSync(LOG_DIR, { recursive: true, mode: 0o755 })
  }
}

/**
 * Rotate log file if it exceeds max size
 */
function rotateLogFile(logFile: string) {
  try {
    const stats = fs.statSync(logFile)
    if (stats.size > MAX_LOG_SIZE) {
      // Rotate existing files
      for (let i = MAX_LOG_FILES - 1; i > 0; i--) {
        const oldFile = `${logFile}.${i}`
        const newFile = `${logFile}.${i + 1}`
        if (fs.existsSync(oldFile)) {
          if (i === MAX_LOG_FILES - 1) {
            fs.unlinkSync(oldFile) // Delete oldest
          } else {
            fs.renameSync(oldFile, newFile)
          }
        }
      }
      // Rotate current file
      fs.renameSync(logFile, `${logFile}.1`)
    }
  } catch {
    // File doesn't exist yet, that's fine
  }
}

/**
 * Write log to file
 */
function writeToFile(logEntry: string, level: string) {
  try {
    ensureLogDir()
    const logFile = path.join(LOG_DIR, `${level.toLowerCase()}.log`)
    rotateLogFile(logFile)
    fs.appendFileSync(logFile, logEntry + '\n')
  } catch {
    // Silent fail - don't break app if logging fails
  }
}

/**
 * Logger class with namespace support
 */
class Logger {
  constructor(private namespace: string) {}

  /**
   * Log error with namespace
   */
  async error(message: string, context?: Record<string, unknown>, userId?: string) {
    const errorLog: ErrorLog = {
      timestamp: new Date(),
      level: 'error',
      namespace: this.namespace,
      message,
      context,
      userId,
      stack: new Error().stack
    }

    const logEntry = JSON.stringify({
      level: 'ERROR',
      namespace: this.namespace,
      message,
      ...context,
      userId,
      timestamp: errorLog.timestamp.toISOString()
    })

    // Console output
    console.error(logEntry)

    // File storage
    writeToFile(logEntry, 'error')

    // Slack notification for critical errors
    if (SLACK_WEBHOOK_URL && SLACK_WEBHOOK_URL !== 'https://hooks.slack.com/YOUR_WEBHOOK') {
      await sendToSlack({
        level: 'error',
        namespace: this.namespace,
        message,
        context,
        userId
      })
    }

    return errorLog
  }

  /**
   * Log warning with namespace
   */
  async warn(message: string, context?: Record<string, unknown>, userId?: string) {
    const warningLog: ErrorLog = {
      timestamp: new Date(),
      level: 'warn',
      namespace: this.namespace,
      message,
      context,
      userId
    }

    const logEntry = JSON.stringify({
      level: 'WARN',
      namespace: this.namespace,
      message,
      ...context,
      userId,
      timestamp: warningLog.timestamp.toISOString()
    })

    // Console output
    console.warn(logEntry)

    // File storage
    writeToFile(logEntry, 'warn')

    return warningLog
  }

  /**
   * Log info with namespace
   */
  info(message: string, context?: Record<string, unknown>) {
    const logEntry = JSON.stringify({
      level: 'INFO',
      namespace: this.namespace,
      message,
      ...context,
      timestamp: new Date().toISOString()
    })

    // Console output
    console.log(logEntry)

    // File storage
    writeToFile(logEntry, 'info')
  }

  /**
   * Log debug with namespace (only in development)
   */
  debug(message: string, context?: Record<string, unknown>) {
    if (process.env.NODE_ENV === 'development') {
      const logEntry = JSON.stringify({
        level: 'DEBUG',
        namespace: this.namespace,
        message,
        ...context,
        timestamp: new Date().toISOString()
      })

      // Console output
      console.log(logEntry)

      // File storage (only in dev)
      writeToFile(logEntry, 'debug')
    }
  }
}

/**
 * Create logger for specific namespace
 */
export function createLogger(namespace: string): Logger {
  return new Logger(namespace)
}

// Pre-configured loggers for common namespaces
export const logger = {
  auth: createLogger('auth'),
  api: createLogger('api'),
  ai: createLogger('ai'),
  db: createLogger('db'),
  stripe: createLogger('stripe'),
  email: createLogger('email'),
  storage: createLogger('storage'),
  monitoring: createLogger('monitoring'),
  telegram: createLogger('telegram'),
  intents: createLogger('intents')
}

/**
 * @deprecated Use logger.api.error() instead
 */
export async function logError(
  message: string,
  context?: Record<string, unknown>,
  userId?: string
) {
  return logger.api.error(message, context, userId)
}

/**
 * @deprecated Use logger.api.warn() instead
 */
export async function logWarning(
  message: string,
  context?: Record<string, unknown>,
  userId?: string
) {
  return logger.api.warn(message, context, userId)
}

/**
 * @deprecated Use logger.api.info() instead
 */
export function logInfo(message: string, context?: Record<string, unknown>) {
  logger.api.info(message, context)
}

/**
 * Send notification to Slack
 */
async function sendToSlack(log: Partial<ErrorLog>) {
  if (!SLACK_WEBHOOK_URL || SLACK_WEBHOOK_URL === 'https://hooks.slack.com/YOUR_WEBHOOK') {
    return
  }

  try {
    const color = log.level === 'error' ? 'danger' : log.level === 'warn' ? 'warning' : 'good'

    const payload = {
      username: 'ZeroSnag Monitor',
      icon_emoji: ':robot_face:',
      attachments: [
        {
          color,
          title: `[${log.level?.toUpperCase()}] [${log.namespace || 'unknown'}] ${log.message}`,
          fields: [
            {
              title: 'Time',
              value: new Date().toISOString(),
              short: true
            },
            {
              title: 'Namespace',
              value: log.namespace || 'N/A',
              short: true
            },
            {
              title: 'User ID',
              value: log.userId || 'N/A',
              short: true
            },
            ...(log.context
              ? Object.entries(log.context).map(([key, value]) => ({
                  title: key,
                  value: JSON.stringify(value),
                  short: true
                }))
              : [])
          ]
        }
      ]
    }

    await fetch(SLACK_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    // Don't use console for Slack success (that's meta-logging!)
  } catch {
    // Don't use console for Slack failures either
  }
}

/**
 * Dispatch a Slack alert for an intent stuck > 24h. Called from the
 * /api/internal/intent-stuck-alert endpoint after the server-side dedupe
 * layer has confirmed the alert hasn't fired in the last 24h.
 *
 * Client-side dedupe would be per-tab + per-device — one stuck intent could
 * fan out to hundreds of identical Slack messages. Dedupe MUST be server-side.
 */
export async function notifyIntentStuck(data: {
  intentId: string
  intentType: string
  ageMs: number
  attempts: number
  userId?: string
}) {
  const message = `Intent stuck > 24h: ${data.intentType}`
  const context = {
    intentId: data.intentId,
    intentType: data.intentType,
    ageHours: (data.ageMs / (60 * 60 * 1000)).toFixed(1),
    attempts: data.attempts,
  }

  await logger.intents.warn(message, context, data.userId)

  if (!SLACK_WEBHOOK_URL || SLACK_WEBHOOK_URL === 'https://hooks.slack.com/YOUR_WEBHOOK') {
    return
  }

  await sendToSlack({
    level: 'warn',
    namespace: 'intents',
    message: `🐢 Intent ${data.intentId.slice(0, 8)} (${data.intentType}) stuck ${context.ageHours}h, ${data.attempts} attempts`,
    context,
    userId: data.userId,
  })
}

/**
 * Monitor Fair Use violations
 */
export async function notifyFairUseViolation(
  userId: string,
  analysisCount: number,
  limit: number
) {
  const message = `Fair Use Limit Exceeded`
  const context = {
    userId,
    analysisCount,
    limit,
    percentage: ((analysisCount / limit) * 100).toFixed(1) + '%'
  }

  await logger.monitoring.warn(message, context, userId)

  // Send admin notification
  if (SLACK_WEBHOOK_URL && SLACK_WEBHOOK_URL !== 'https://hooks.slack.com/YOUR_WEBHOOK') {
    await sendToSlack({
      level: 'warn',
      namespace: 'monitoring',
      message: `🚨 User ${userId} exceeded Fair Use Limit (${analysisCount}/${limit})`,
      context
    })
  }
}

/**
 * Monitor system health
 */
export async function healthCheck() {
  const checks = {
    timestamp: new Date().toISOString(),
    database: false,
    stripe: false,
    resend: false,
    supabase: false
  }

  try {
    // Database check (Supabase)
    const { supabaseAdmin } = await import('./supabase')
    const { error } = await supabaseAdmin.from('users').select('id').limit(1)
    checks.database = !error
    if (error) {
      logger.monitoring.error('Database health check failed', { error: String(error) })
    }
  } catch (error) {
    logger.monitoring.error('Database health check failed', { error: String(error) })
  }

  try {
    // Stripe check
    const { stripe } = await import('./stripe')
    if (stripe) {
      checks.stripe = true
    }
  } catch (error) {
    logger.monitoring.error('Stripe health check failed', { error: String(error) })
  }

  try {
    // Resend check
    const { resend } = await import('./resend')
    if (resend) {
      checks.resend = true
    }
  } catch (error) {
    logger.monitoring.error('Resend health check failed', { error: String(error) })
  }

  return checks
}

/**
 * Send checkout notification to Slack (for sales team)
 */
export async function notifyCheckoutStarted(data: {
  companyName: string
  contactName: string
  email: string
  userCount: number
  phone?: string
}) {
  if (!SLACK_WEBHOOK_URL || SLACK_WEBHOOK_URL === 'https://hooks.slack.com/YOUR_WEBHOOK') {
    logger.monitoring.info('Checkout started (Slack not configured)', data)
    return
  }

  try {
    const payload = {
      username: 'Zerosnag Sales',
      icon_emoji: ':eyes:',
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: '👀 Neuer Checkout gestartet!',
            emoji: true
          }
        },
        {
          type: 'section',
          fields: [
            { type: 'mrkdwn', text: `*Firma:*\n${data.companyName}` },
            { type: 'mrkdwn', text: `*Kontakt:*\n${data.contactName}` },
            { type: 'mrkdwn', text: `*E-Mail:*\n${data.email}` },
            { type: 'mrkdwn', text: `*Nutzer:*\n${data.userCount}` },
            ...(data.phone ? [{ type: 'mrkdwn', text: `*Telefon:*\n${data.phone}` }] : [])
          ]
        },
        {
          type: 'context',
          elements: [
            { type: 'mrkdwn', text: `_Wartet auf Zahlung..._` }
          ]
        }
      ]
    }

    await fetch(SLACK_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    logger.monitoring.info('Checkout started notification sent', data)
  } catch (error) {
    logger.monitoring.error('Failed to send checkout started notification', { error: String(error) })
  }
}

/**
 * Send payment success notification to Slack (for sales team)
 */
export async function notifyCheckoutPaid(data: {
  companyName: string
  contactName: string
  email: string
  userCount: number
  amount: number
  currency: string
  signupRequestId?: string
}) {
  if (!SLACK_WEBHOOK_URL || SLACK_WEBHOOK_URL === 'https://hooks.slack.com/YOUR_WEBHOOK') {
    logger.monitoring.info('Checkout paid (Slack not configured)', data)
    return
  }

  try {
    const formattedAmount = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: data.currency.toUpperCase()
    }).format(data.amount / 100)

    const payload = {
      username: 'Zerosnag Sales',
      icon_emoji: ':moneybag:',
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: '💰 Neuer Kunde hat bezahlt!',
            emoji: true
          }
        },
        {
          type: 'section',
          fields: [
            { type: 'mrkdwn', text: `*Firma:*\n${data.companyName}` },
            { type: 'mrkdwn', text: `*Kontakt:*\n${data.contactName}` },
            { type: 'mrkdwn', text: `*E-Mail:*\n${data.email}` },
            { type: 'mrkdwn', text: `*Nutzer:*\n${data.userCount}` },
            { type: 'mrkdwn', text: `*Betrag:*\n${formattedAmount}` }
          ]
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: '⚡ *Aktion erforderlich:* Bitte Organisation & Admin-Account einrichten und Kunden kontaktieren!'
          }
        }
      ]
    }

    await fetch(SLACK_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    logger.monitoring.info('Checkout paid notification sent', data)
  } catch (error) {
    logger.monitoring.error('Failed to send checkout paid notification', { error: String(error) })
  }
}

/**
 * Notify the team (Slack) that a new demo account has self-registered and is
 * awaiting admin approval. Best-effort — never throws.
 */
export async function notifyDemoRegistration(data: {
  name: string
  company: string
  email: string
  phone: string
  /** auto_approve_demo was on: nothing to click, the account activates itself. */
  autoApproved?: boolean
}) {
  if (!SLACK_WEBHOOK_URL || SLACK_WEBHOOK_URL === 'https://hooks.slack.com/YOUR_WEBHOOK') {
    logger.monitoring.info('Demo registration (Slack not configured)', data)
    return
  }
  try {
    const payload = {
      username: 'Zerosnag Sales',
      icon_emoji: ':wave:',
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: data.autoApproved
              ? '🆕 Neue Demo-Registrierung – automatisch freigeschaltet'
              : '🆕 Neue Demo-Registrierung – Freischaltung nötig',
            emoji: true,
          },
        },
        {
          type: 'section',
          fields: [
            { type: 'mrkdwn', text: `*Name:*\n${data.name}` },
            { type: 'mrkdwn', text: `*Firma:*\n${data.company}` },
            { type: 'mrkdwn', text: `*E-Mail:*\n${data.email}` },
            { type: 'mrkdwn', text: `*Telefon:*\n${data.phone}` },
          ],
        },
        {
          type: 'context',
          elements: [{
            type: 'mrkdwn',
            text: data.autoApproved
              ? '_Keine Aktion nötig — der Zugang wird bei der ersten Anmeldung aktiviert._'
              : '_Im Admin-Bereich unter „Freischaltung" prüfen und freigeben._',
          }],
        },
      ],
    }
    await fetch(SLACK_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    logger.monitoring.info('Demo registration notification sent', { email: data.email })
  } catch (error) {
    logger.monitoring.error('Failed to send demo registration notification', { error: String(error) })
  }
}

/**
 * Performance monitoring
 */
export class PerformanceMonitor {
  private startTime: number
  private operation: string
  private namespace: string

  constructor(operation: string, namespace: string = 'api') {
    this.operation = operation
    this.namespace = namespace
    this.startTime = Date.now()
  }

  end() {
    const duration = Date.now() - this.startTime
    const log = createLogger(this.namespace)
    log.info(`Performance: ${this.operation}`, { duration: `${duration}ms` })

    // Alert if operation took too long
    if (duration > 30000) {
      // 30 seconds
      log.warn(`Slow operation detected: ${this.operation}`, { duration: `${duration}ms` })
    }

    return duration
  }
}

/**
 * Track API request metrics
 */
export function trackAPIRequest(
  endpoint: string,
  method: string,
  statusCode: number,
  duration: number
) {
  logger.api.info('API Request', {
    endpoint,
    method,
    statusCode,
    duration: `${duration}ms`
  })

  // Alert on errors
  if (statusCode >= 500) {
    logger.api.error(`API Error: ${method} ${endpoint}`, {
      statusCode,
      duration: `${duration}ms`
    })
  }
}

/**
 * Per-request timing record. Written to perf.log only when slow OR sampled,
 * so volume stays bounded even at ~3M req/hour. Grep by userId to filter.
 */
const PERF_SLOW_MS = Number(process.env.PERF_SLOW_MS ?? 1000)
const PERF_SAMPLE_RATE = Number(process.env.PERF_SAMPLE_RATE ?? 0.01)

export interface RequestTimingEntry {
  userId?: string | null
  endpoint: string
  method: string
  status: number
  durationMs: number
  error?: string
}

export function recordRequestTiming(entry: RequestTimingEntry) {
  const slow = entry.durationMs >= PERF_SLOW_MS
  const sampled = !slow && Math.random() < PERF_SAMPLE_RATE
  if (!slow && !sampled) return

  const line = JSON.stringify({
    level: slow ? 'SLOW' : 'SAMPLE',
    namespace: 'perf',
    userId: entry.userId ?? null,
    endpoint: entry.endpoint,
    method: entry.method,
    status: entry.status,
    durationMs: entry.durationMs,
    error: entry.error,
    timestamp: new Date().toISOString(),
  })
  writeToFile(line, 'perf')
}